import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PageTitle from "../../../components/partials/PageTitle";
import ReportFilter from "./CustomerReport/ReportFilter";
import excel from "../../../assets/images/img-icons/excel.png";
import pdf from "../../../assets/images/img-icons/pdf.png";
import { getTrialBalance } from "../../../redux/slice/report/reportSlice";
import { useDispatch } from "react-redux";
import { useReactToPrint } from "react-to-print";
import { CSVLink } from "react-csv";

const TrialBalance = () => {
  const CCPdfRef = useRef();

  const [filterData, setFilterData] = useState();
  const [filteredItem, setFilteredItem] = useState(false);

  // Define a state variable to store group sums
  const [groupSums, setGroupSums] = useState({});

  const { customerOverviewData } = useSelector((state) => state.report);
  const { user } = useSelector((state) => state?.auth?.user);
  const {
    offices: { offices },
  } = useSelector((state) => state.office);

  const dispatch = useDispatch();

  function combineArrays(array1, array2) {
    return array1?.map((item1) => {
      const matchingItem = array2.find(
        (item2) => item2.gl_code === item1.gl_code
      );
      return { ...item1, ...matchingItem };
    });
  }

  const combinedData = combineArrays(
    customerOverviewData?.credit_debit_balance,
    customerOverviewData?.opening_closing_balance
  );

  const breadcrumb = {
    from: {
      title: "Financial Report",
      link: "/admin/reports/financial-report",
    },
    current: {
      title: "Trial Balance",
      link: null,
    },
  };

  function findOfficeById(offices, officeId) {
    for (const office of offices) {
      if (office.id === officeId) {
        return office;
      }
    }
    // Return null if no office with the specified ID is found
    return null;
  }

  const groupedData = combinedData
    ? combinedData.reduce((result, item) => {
        const { group_name, ...data } = item;
        if (group_name !== null) {
          if (!result[group_name]) {
            result[group_name] = [];
          }
          result[group_name].push(data);
        }
        return result;
      }, {})
    : null;

  // Create an array of valid groups
  const validGroups = Object.entries(groupedData || {});

  const headers = [
    { label: "GL Code", key: "gl_code" },
    { label: "GL Group", key: "group_name" },
    { label: "GL Account Name", key: "account_name" },
    { label: "Opening Balance", key: "opening_balance" },
    { label: "Debit", key: "debit_amount" },
    { label: "Credit", key: "credit_amount" },
    { label: "Closing Balance", key: "closing_balance" },
  ];

  // Create an array for processed data
  const processedData = [];

  let foundOffice;

  if (customerOverviewData) {
    foundOffice = findOfficeById(offices, filterData?.office_id);
    validGroups.forEach(([groupName, groupItems]) => {
      if (groupName) {
        // Initialize sum variables for the group
        let openingBalanceSum = 0;
        let debitSum = 0;
        let creditSum = 0;
        let closingBalanceSum = 0;

        // Iterate through groupItems and calculate the sums
        groupItems.forEach((item) => {
          openingBalanceSum += parseFloat(item.opening_balance) || 0;
          debitSum += parseFloat(item.total_debit_amount_from_journals) || 0;
          creditSum += parseFloat(item.total_credit_amount_from_journals) || 0;
          closingBalanceSum += parseFloat(item.closing_balance) || 0;

          // Add rows for group items
          processedData.push({
            gl_code: item.gl_code || "",
            group_name: item.group_name || "",
            account_name: item.account_name || "",
            opening_balance: item.opening_balance || "",
            debit_amount: item.total_debit_amount_from_journals || "",
            credit_amount: item.total_credit_amount_from_journals || "",
            closing_balance: item.closing_balance || "",
          });
        });

        // Add a row for the group sums
        processedData.push({
          gl_code: "",
          group_name: `Total ${groupName}`,
          account_name: "",
          opening_balance: openingBalanceSum.toFixed(2), // Format as needed
          debit_amount: debitSum.toFixed(2), // Format as needed
          credit_amount: creditSum.toFixed(2), // Format as needed
          closing_balance: closingBalanceSum.toFixed(2), // Format as needed
        });
      }
    });
  }

  useEffect(() => {
    if (filteredItem) {
      if (filterData.office_id === "") {
        const updatedFilterData = {
          ...filterData,
          office_id: user?.office_id,
        };
        dispatch(getTrialBalance(updatedFilterData));
      } else {
        dispatch(getTrialBalance(filterData));
      }
    }
  }, [dispatch, filterData, filteredItem, user?.office_id]);

  const generatePDF = useReactToPrint({
    content: () => CCPdfRef.current,
    documentTitle: "Customer Payment",
  });

  // Calculate the sums for each group
  useEffect(() => {
    if (customerOverviewData) {
      const sums = {};

      validGroups.forEach(([groupName, groupItems]) => {
        if (groupName) {
          sums[groupName] = groupItems.reduce(
            (acc, item) => ({
              opening_balance:
                acc.opening_balance + parseFloat(item.opening_balance) || 0,
              debit:
                acc.debit + parseFloat(item.total_debit_amount_from_journals) ||
                0,
              credit:
                acc.credit +
                  parseFloat(item.total_credit_amount_from_journals) || 0,
              closing_balance:
                acc.closing_balance + parseFloat(item.closing_balance) || 0,
            }),
            {
              opening_balance: 0,
              debit: 0,
              credit: 0,
              closing_balance: 0,
            }
          );
        }
      });

      setGroupSums(sums);
    }
  }, [customerOverviewData]);

  return (
    <>
      {/* page title */}
      <PageTitle title='Trial Balance' breadcrumb={breadcrumb} />
      {/* end page title */}
      <div className='row card p-1' style={{ margin: "auto 1px" }}>
        <ReportFilter
          page={"trialBalance"}
          setFilterData={setFilterData}
          setFilteredItem={setFilteredItem}
          filteredItem={filteredItem}
        />
        {customerOverviewData && (
          <div className='row mb-3'>
            <>
              <div className='d-flex flex-column bg-primary p-1 justify-content-center align-items-center my-2'>
                <h4
                  className='text-uppercase text-white fw-bold'
                  style={{ fontSize: "30px" }}
                >
                  Trial Balance
                </h4>
              </div>
              {customerOverviewData?.credit_debit_balance?.length > 0 && (
                <div className='d-flex justify-content-between align-items-center'>
                  <div>
                    <div>From: {filterData?.start_date}</div>
                  </div>
                  <div>
                    <div>End: {filterData?.end_date}</div>
                  </div>
                  <div>
                    <div>Office: {foundOffice?.office_name}</div>
                  </div>
                  <div className=' d-flex align-items-center border '>
                    <span
                      className='bg-info me-2 text-white'
                      style={{ padding: "3px" }}
                    >
                      Export to
                    </span>

                    <span
                      href='#'
                      id='btnExport'
                      className='me-1 cursor-p'
                      title='Export to Pdf'
                      onClick={generatePDF}
                    >
                      <img
                        src={pdf}
                        alt='export-pdf'
                        style={{ height: "25px" }}
                      />
                    </span>
                    {customerOverviewData ? (
                      <CSVLink
                        data={processedData}
                        headers={headers}
                        style={{ fontWeight: "bolder", cursor: "pointer" }}
                        filename='Trial Balance Report'
                      >
                        <span
                          href='#'
                          id='btnExport'
                          className='mr-1'
                          title='Export to Excel'
                        >
                          <img
                            src={excel}
                            alt='export-excel'
                            style={{ height: "25px", marginRight: "5px" }}
                          />
                        </span>
                      </CSVLink>
                    ) : null}
                  </div>
                </div>
              )}
              <div className='table-responsive'>
                <table
                  className='table table-centered nowrap w-100 no-footer'
                  ref={CCPdfRef}
                >
                  <thead>
                    <tr>
                      <th
                        className='sorting'
                        tabIndex='0'
                        rowSpan='1'
                        colSpan='1'
                        style={{ width: "100.8px" }}
                        aria-label='Email: activate to sort column ascending'
                      >
                        GL Code
                      </th>
                      <th
                        className='sorting'
                        tabIndex='0'
                        rowSpan='1'
                        colSpan='1'
                        style={{ width: "150.8px" }}
                        aria-label='Customer: activate to sort column ascending'
                      >
                        GL Group
                      </th>
                      <th
                        className='sorting'
                        tabIndex='0'
                        rowSpan='1'
                        colSpan='1'
                        style={{ width: "164.8px" }}
                        aria-label='Phone: activate to sort column ascending'
                      >
                        GL Account Name
                      </th>
                      <th
                        className='sorting'
                        tabIndex='0'
                        rowSpan='1'
                        colSpan='1'
                        style={{ width: "144.8px" }}
                        aria-label='Phone: activate to sort column ascending'
                      >
                        Opening Balance
                      </th>
                      <th
                        className='sorting'
                        tabIndex='0'
                        rowSpan='1'
                        colSpan='1'
                        style={{ width: "120.8px" }}
                        aria-label='Status: activate to sort column ascending'
                      >
                        Debit
                      </th>
                      <th
                        className='sorting'
                        tabIndex='0'
                        rowSpan='1'
                        colSpan='1'
                        style={{ width: "90.8px" }}
                        aria-label='Status: activate to sort column ascending'
                      >
                        Credit
                      </th>
                      <th
                        className='sorting'
                        tabIndex='0'
                        rowSpan='1'
                        colSpan='1'
                        style={{ width: "120.8px" }}
                        aria-label='Status: activate to sort column ascending'
                      >
                        Closing Balance
                      </th>
                    </tr>
                  </thead>
                  {customerOverviewData?.credit_debit_balance?.length &&
                  groupedData ? (
                    <tbody>
                      {validGroups.map(([groupName, groupItems], i) => (
                        <React.Fragment key={i}>
                          {groupName && ( // Check if groupName is not null
                            <tr className='text-white bg-primary'>
                              <td colSpan='1' className='fw-bold'></td>
                              <td colSpan='' className='fw-bold'>
                                {groupName}
                              </td>
                              <td colSpan='1' className='fw-bold'></td>
                              <td className='fw-bold'>
                                {groupSums[groupName]?.opening_balance.toFixed(
                                  2
                                )}
                              </td>
                              <td className='fw-bold'>
                                {groupSums[groupName]?.debit.toFixed(2)}
                              </td>
                              <td className='fw-bold'>
                                {groupSums[groupName]?.credit.toFixed(2)}
                              </td>
                              <td className='fw-bold'>
                                {groupSums[groupName]?.closing_balance.toFixed(
                                  2
                                )}
                              </td>
                            </tr>
                          )}
                          {groupItems?.map((item, j) => (
                            <tr key={j}>
                              <td className='text-dark'>{item?.gl_code}</td>
                              <td className='text-dark'>{item?.group_name}</td>
                              <td className='text-dark'>
                                {item?.account_name}
                              </td>
                              <td className='text-dark'>
                                {item?.opening_balance}
                              </td>
                              <td className='text-dark'>
                                {item?.total_debit_amount_from_journals}
                              </td>
                              <td className='text-dark'>
                                {item?.total_credit_amount_from_journals}
                              </td>
                              <td className='text-dark'>
                                {item?.closing_balance}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  ) : null}
                </table>
                {customerOverviewData?.length < 1 ? (
                  <h5 className='alert alert-info'>No result found!</h5>
                ) : null}
              </div>
            </>
          </div>
        )}
      </div>
    </>
  );
};

export default TrialBalance;
